import React, { useEffect, useState, useRef, lazy, Suspense } from "react";
import { observer } from "mobx-react-lite";
import { DataTableComp, DataTableLoading } from "../../../../app/common/common-components/OMSDatatables";
import apiAgent from "../../../../app/api/apiAgent";
import DetailModalPopup from "../../../../app/common/common-components/DetailModalPopup";
import PropertyReportsStore from "../../../../app/stores/propertyReportsStore";
import { ClipLoader } from "react-spinners";
import { PropertyReportModel } from "../../../../app/models/PropertyReportModel";
import { toast } from "react-toastify";

// Lazy load the ReportOwnerDetails component
const ReportOwnerDetails = lazy(() => import('./ReportOwnerDetails'));

interface Props {
  isLoading: boolean;
  propertyReportsStore: PropertyReportsStore;
}

const CenteredSpinner: React.FC = () => (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 9999,
  }}>
    <ClipLoader size={50} color={"#ffffff"} loading={true} />
  </div>
);

const PreviousReportsComponent: React.FC<Props> = observer(({ isLoading, propertyReportsStore }) => {
  const [showOwnerDetailsModal, setShowOwnerDetailsModal] = useState(false);
  const [selectedOwnerDetails, setSelectedOwnerDetails] = useState<{ [key: string]: any } | null>(null);
  const [loadingReportId, setLoadingReportId] = useState<string | null>(null);
  const dataTableRef = useRef<DataTableComp>(null);
  
  // State for ReportOwnerDetails popup
  const [showReportOwnerDetails, setShowReportOwnerDetails] = useState(false);
  const [ownerDetailsPropertyId, setOwnerDetailsPropertyId] = useState('');
  const [ownerDetailsPropertyType, setOwnerDetailsPropertyType] = useState('');
  const [ownerDetailsStreetAddress, setOwnerDetailsStreetAddress] = useState('');
  const [ownerDetailsData, setOwnerDetailsData] = useState<any>(null);

  useEffect(() => {
    //console.log("inside PreviousReportsComponent, propertyReports: ", propertyReportsStore.propertyReports);
  }, [propertyReportsStore.propertyReports]);

  const generateFileName = (streetAddress: string): string => {
    let address = streetAddress || "Unknown Address";
    if (address.length > 35) {
      address = address.substring(0, 35);
    }
  
    const formattedAddress = address.trim().replace(/\s+/g, " ");
    const date = new Date().toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "2-digit" }).replace(/ /g, " ");
    return `Property Insights - ${formattedAddress} - ${date}.pdf`;
  };


  const handleReportLinkClick = (reportId: string) => {
    setLoadingReportId(reportId);
  
    const selectedReport = propertyReportsStore.propertyReports.find((report) => report.reportId === reportId);
    if (!selectedReport) {
      toast.error("Report not found!");
      setLoadingReportId(null);
      return;
    }
  
    const fileName = generateFileName(selectedReport.streetAddress);
  
    apiAgent.PropertyReports.downloadReport(reportId)
      .then((response) => {
        const binaryData = [];
        binaryData.push(response.data);
        const url = window.URL.createObjectURL(new Blob(binaryData, { type: "application/pdf" }));
  
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
  
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      })
      .catch((error) => {
        console.error("Error inside listing report", error);
      })
      .finally(() => {
        setLoadingReportId(null);
      });
  };
    
  const handleOwnerLinkClick = (propertyId: string) => {
    const property = propertyReportsStore.propertyReports.find((report) => report.propertyId === propertyId);

    if (property && property.ownerDetails) {
      const ownerDetailsArray: Array<{ [key: string]: any }> = JSON.parse(property.ownerDetails);

      if (ownerDetailsArray.length > 0) {
        const formatedArray = ownerDetailsArray.map(formatKeysInObject);
        setSelectedOwnerDetails(formatedArray);
        setShowOwnerDetailsModal(true);
      }
    }
  };

  // Function to handle opening the ReportOwnerDetails popup
  const handleOwnerDetailsButtonClick = (propertyId: string) => {
    const property = propertyReportsStore.propertyReports.find((report) => report.propertyId === propertyId);
    
    if (property) {
      let ownerDetailsData = null;
      
      // Parse the owner details if it's a string and available
      if (property.ownerDetails) {
        try {
          if (typeof property.ownerDetails === 'string') {
            ownerDetailsData = JSON.parse(property.ownerDetails);
          } else {
            ownerDetailsData = property.ownerDetails;
          }
        } catch (error) {
          console.error("Error parsing owner details:", error);
        }
      }
      
      // Set the data for the ReportOwnerDetails component
      setOwnerDetailsPropertyId(property.propertyId);
      setOwnerDetailsPropertyType(property.propertyType);
      setOwnerDetailsStreetAddress(property.streetAddress);
      setOwnerDetailsData(ownerDetailsData);
      setShowReportOwnerDetails(true);
    }
  };

  const formatKeysInObject = (obj: { [key: string]: any }): { [key: string]: any } => {
    const formattedObj: { [key: string]: any } = {};
    Object.entries(obj).forEach(([key, value]) => {
      const formattedKey = key.replace(/([A-Z])/g, " $1").trim().replace(/^./, (str) => str.toUpperCase());
      formattedObj[formattedKey] = value;
    });
    return formattedObj;
  };

  const columns = [
    { 
      data: "streetAddress", 
      title: "Street Address", 
      className: "dt-center",
      render: (data: string, type: any, row: PropertyReportModel) => {
        // Create a pill for property type
        const propertyTypePill = `
          <span style="
            display: inline-block;
            background-color: #f8f9fa;
            color: #6c757d;
            border-radius: 12px;
            padding: 2px 8px;
            font-size: 0.75rem;
            margin-left: 8px;
            font-weight: 500;
            vertical-align: middle;
          ">
            ${row.propertyType || 'Unknown'}
          </span>
        `;
        
        return `<div style="display: flex; align-items: center; justify-content: flex-start;">
          <span>${data || ''}</span>
          ${propertyTypePill}
        </div>`;
      }
    },
    {
      data: null,
      title: "Purchased Reports",
      className: "dt-center",
      render: (data: any, type: any, row: PropertyReportModel) => {
        // Format property report button if available
        const propertyButton = row.reportGenerationDate
          ? `<button
              class="btn btn-default property-report-button"
              data-report-id="${row.reportId}"
              style="
                color: var(--color2);
                border: 1px solid var(--color2);
                display: flex;
                align-items: center;
                margin-right: ${row.ownerDetailsGenerationDate ? "5px" : "0"}
              "
            >
              <i class="fa fa-home" style="margin-right: 5px;"></i>
              ${new Date(row.reportGenerationDate)
                .toLocaleDateString("en-GB", { 
                  day: "2-digit", 
                  month: "short", 
                  year: "numeric" 
                })} - Property Report
            </button>`
          : "";

        // Format owner details button if available
        const ownerButton = row.ownerDetailsGenerationDate
          ? `<button
              class="btn btn-default owner-details-button"
              data-id="${row.propertyId}"
              data-street-address="${row.streetAddress}"
              data-property-type="${row.propertyType}"
              style="
                color: var(--color1);
                border: 1px solid var(--color1);
                display: flex;
                align-items: center;
                margin-left: ${propertyButton ? "5px" : "0"}
              "
            >
              <i class="fa fa-address-card-o" style="margin-right: 5px;"></i>
              ${new Date(row.ownerDetailsGenerationDate)
                .toLocaleDateString("en-GB", { 
                  day: "2-digit", 
                  month: "short", 
                  year: "numeric" 
                })} - Owner Details
            </button>`
          : "";

        // Return both buttons in a container - horizontal layout when both exist
        return `
          <div class="reports-container" style="
            display: flex; 
            flex-direction: ${propertyButton && ownerButton ? "row" : "column"}; 
            align-items: flex-start;
            justify-content: flex-start;
            flex-wrap: wrap;
            gap: 5px;
          ">
            ${propertyButton}
            ${ownerButton}
          </div>
        `;
      },
    },
  ];

  useEffect(() => {
    const tableId = "#previousReportsTable";
    
    // Update event handler for property report button
    $(tableId).on("click", ".property-report-button", function () {
      const reportId = $(this).data("report-id").toString();
      handleReportLinkClick(reportId);
    });

    // Keep the owner details button handler
    $(tableId).on("click", ".owner-details-button", function () {
      const propertyId = $(this).data("id").toString();
      handleOwnerDetailsButtonClick(propertyId);
    });

    return () => {
      $(tableId).off("click", ".property-report-button");
      $(tableId).off("click", ".owner-details-button");
      $(tableId).off("click", ".report-link");
      $(tableId).off("click", ".owner-link");
    };
  }, [propertyReportsStore.propertyReports]);

  useEffect(() => {
    if (dataTableRef.current) {
      dataTableRef.current.reloadTableData(propertyReportsStore.propertyReports);
    }
  }, [propertyReportsStore.propertyReports]);

  return (
    <div className="panel-body table-responsive left-align">
       <DataTableComp
        ref={dataTableRef}
        id="previousReportsTable"
        data={propertyReportsStore.propertyReports}
        columns={columns}
        columnDefs={[]}
        order={[]} // Remove the default sorting
        ordering={false} // Disable sorting
        showSearchBox={false}
      />
      <DataTableLoading loading={isLoading} />

      <DetailModalPopup
        show={showOwnerDetailsModal}
        onClose={() => setShowOwnerDetailsModal(false)}
        title="Owner Details"
        data={selectedOwnerDetails}
      />

      {loadingReportId && <CenteredSpinner />}
      
      {/* ReportOwnerDetails Popup */}
      {showReportOwnerDetails && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 9999,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <div className="owner-details-container" style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '20px',
            maxWidth: '800px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative'
          }}>
            <button 
              className="owner-details-close-btn" 
              onClick={() => setShowReportOwnerDetails(false)}
              aria-label="Close"
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer'
              }}
            >
              ×
            </button>
            <Suspense fallback={<div>Loading owner details...</div>}>
              <ReportOwnerDetails 
                propertyId={ownerDetailsPropertyId}
                propertyType={ownerDetailsPropertyType}
                streetAddress={ownerDetailsStreetAddress}
                ownerDetailsData={ownerDetailsData}
              />
            </Suspense>
          </div>
        </div>
      )}
    </div>
  );
});

export default PreviousReportsComponent;